/**
 * Agent Portal Login — navy+gold AI Search Portal branding.
 * KOPS-511: matches ForgotPasswordAIForward / ResetPasswordAIForward.
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignInAlt, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import agentPortalService from '../../services/agentPortal.service';

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c',
};

const fonts = {
  display: "'Bebas Neue', Impact, 'Arial Black', sans-serif",
  body: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
};

const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';

const isKairosIQDomain = typeof window !== 'undefined' && (
  window.location.hostname.includes('app.kairosiq.ai') ||
  window.location.hostname.includes('demo.kairosiq.ai') ||
  window.location.hostname.includes('dev.app.kairosiq.ai') ||
  new URLSearchParams(window.location.search).get('brand') === 'kairosiq'
);

const AgentLogin: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, setCookie] = useCookies(['isLoggedInKIQ']);
  const [email, setEmail] = useState(() => localStorage.getItem('rememberedEmail') || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('rememberedEmail'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await agentPortalService.login({ email, password, rememberMe });

      if (response.success && response.token) {
        // Save or clear remembered email
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        setCookie('isLoggedInKIQ', true, { path: '/' });

        // Activate broker invitation if present
        const inviteToken = searchParams.get('invite_token');
        if (inviteToken) {
          try {
            await axios.post('/api/broker/invitations/accept-invitation', {
              token: inviteToken,
              email,
            }, { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${response.token}` } });
            console.log('✅ Broker invitation activated after login');
          } catch (invErr) {
            console.warn('⚠️ Broker invitation activation failed:', invErr);
          }
        }

        // Role-based redirect
        const userRole = (response.user?.user_role || response.user?.role || '').toUpperCase();
        if (userRole === 'LENDER' || userRole === 'SPONSOR') {
          navigate('/sponsor/dashboard');
        } else if (userRole === 'BROKER' || userRole === 'MANAGER') {
          navigate('/broker-intelligence/dashboard');
        } else {
          navigate('/agent-portal/dashboard');
        }
      } else {
        setError(response.message || 'Login failed. Please check your credentials.');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to sign in. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.navy,
        fontFamily: fonts.body,
        color: colors.off,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}
    >
      <link href={FONT_LINK} rel="stylesheet" />

      <div style={{ width: '100%', maxWidth: '480px' }}>
        {/* Card */}
        <div
          style={{
            background: colors.navy2,
            border: `1px solid ${colors.navy3}`,
            borderTop: `4px solid ${colors.gold}`,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
          }}
        >
          {/* Header */}
          <div style={{ textAlign: 'center', padding: '40px 32px 24px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ fontFamily: fonts.display, fontSize: '32px', letterSpacing: '3px', color: colors.white, marginBottom: '6px' }}>
              {isKairosIQDomain ? (
                <>KAIROS<span style={{ color: colors.gold2 }}>IQ</span></>
              ) : (
                <>AI<span style={{ color: colors.gold2 }}>Search</span>Portal</>
              )}
            </div>
            <div style={{ color: colors.muted, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '28px' }}>
              {isKairosIQDomain ? 'AI-Powered Intelligence Platform' : 'AI-Powered Real Estate Compliance Platform'}
            </div>

            <div
              style={{
                width: '72px', height: '72px', borderRadius: '50%',
                background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 50%, ${colors.gold3} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px auto', fontSize: '32px', color: colors.navy,
                boxShadow: '0 8px 24px rgba(201, 149, 42, 0.35)',
              }}
            >
              <FontAwesomeIcon icon={faSignInAlt} style={{ fontSize: '28px' }} />
            </div>

            <h1 style={{ fontFamily: fonts.display, fontSize: '32px', letterSpacing: '1.5px', color: colors.white, margin: '0 0 8px', lineHeight: 1.15 }}>
              Welcome Back
            </h1>
            <p style={{ fontSize: '13px', color: colors.muted, margin: 0, lineHeight: 1.5 }}>
              {isKairosIQDomain ? 'Sign in to your dashboard' : 'Sign in to manage your deals, clients, and compliance'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
            {error && (
              <div
                style={{
                  background: `${colors.red}22`, border: `1px solid ${colors.red}`,
                  borderLeft: `3px solid ${colors.red}`, color: colors.red,
                  padding: '12px 14px', borderRadius: '6px', fontSize: '12px',
                  fontWeight: 600, lineHeight: 1.5, marginBottom: '18px',
                }}
              >
                {error}
              </div>
            )}

            <label htmlFor="email" style={{ display: 'block', fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700, marginBottom: '8px' }}>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@brokerage.com"
              required
              autoFocus
              autoComplete="email"
              style={{
                width: '100%', background: colors.navy3, color: colors.white,
                border: `1px solid ${colors.navy3}`, borderRadius: '8px',
                padding: '14px 16px', fontSize: '14px', fontFamily: fonts.body,
                outline: 'none', marginBottom: '16px', boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = colors.gold)}
              onBlur={(e) => (e.currentTarget.style.borderColor = colors.navy3)}
            />

            <label htmlFor="password" style={{ display: 'block', fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700, marginBottom: '8px' }}>
              Password
            </label>
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                style={{
                  width: '100%', background: colors.navy3, color: colors.white,
                  border: `1px solid ${colors.navy3}`, borderRadius: '8px',
                  padding: '14px 16px', paddingRight: '48px', fontSize: '14px',
                  fontFamily: fonts.body, outline: 'none', boxSizing: 'border-box',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = colors.gold)}
                onBlur={(e) => (e.currentTarget.style.borderColor = colors.navy3)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                style={{
                  position: 'absolute', right: '14px', top: '50%',
                  transform: 'translateY(-50%)', background: 'none',
                  border: 'none', cursor: 'pointer', color: colors.muted,
                  fontSize: '16px', padding: '4px',
                }}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: colors.muted }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Remember me
              </label>
              <a
                href="/agent-portal/forgot-password"
                style={{ color: colors.gold2, fontSize: '12px', textDecoration: 'none', fontWeight: 600 }}
              >
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? colors.muted : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 50%, ${colors.gold3} 100%)`,
                color: colors.navy, border: 'none', borderRadius: '8px',
                padding: '14px 20px', fontSize: '13px', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '1.2px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: fonts.body,
                boxShadow: loading ? 'none' : '0 8px 24px rgba(201, 149, 42, 0.35)',
              }}
            >
              {loading ? 'Signing in…' : 'Sign In →'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <a
                href="/agent-portal/signup"
                style={{ color: colors.gold2, fontSize: '12px', textDecoration: 'none', fontWeight: 600 }}
              >
                Create an account
              </a>
            </div>
          </form>

          {/* Footer */}
          <div style={{ background: colors.navy, padding: '20px 32px', textAlign: 'center', borderTop: `1px solid ${colors.navy3}` }}>
            <div style={{ fontSize: '11px', color: colors.muted, lineHeight: 1.6, marginBottom: '6px' }}>
              🔒 Secure, compliant, and built for real estate professionals
            </div>
            <div style={{ fontSize: '10px', color: colors.muted }}>
              © 2026 {isKairosIQDomain ? 'KairosIQ AI, Inc.' : 'AI Search Portal'}. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentLogin;
